import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>One bottle, two capsules a day.</strong>
      <span className="block text-emerald-800/80 mt-0.5">BCM-95® — clean-label curcumin food supplement, made for active UK adults.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors">
      Try £37.99 first bottle <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const sections = [
  {
    id: 'overview',
    heading: 'Turmeric and curcumin: a UK-friendly overview',
    body: (
      <>
        <p>Turmeric is one of the most familiar spices in British kitchens. Curcumin — the principal yellow polyphenol inside the turmeric rhizome — has become one of the most-studied plant compounds of the last three decades, with thousands of peer-reviewed publications spanning food-science, pharmacology and nutrition.</p>
        <p>For a UK consumer, the relevant question is: <em>what does this mean for me, today, as someone living an active life?</em> This article walks through the most commonly explored use cases, what the published evidence does and doesn't show, and what the UK food-supplement regulations let us actually say on a product label.</p>
        <p className="text-[13px] italic text-slate-500">BCM-95® is a food supplement, not a medicine. It is not intended to diagnose, treat, cure or prevent any disease.</p>
      </>
    ),
  },
  {
    id: 'active-living',
    heading: 'Curcumin in an active-living routine',
    body: (
      <>
        <p>Active adults — from runners and cyclists to padel players and weekend hikers — frequently include curcumin alongside vitamin D, omega-3 and magnesium as part of a polyphenol-rich active-living routine. The thinking is straightforward: support general everyday comfort, recovery and continued movement.</p>
        <p>Curcumin's interest to researchers stems from its activity at the cellular level — modulating signalling pathways involved in normal inflammatory balance. Crucially, this is the kind of <em>physiological</em> support that food supplements can sit alongside; it is not a substitute for the anti-inflammatory medication your GP might prescribe for a specific diagnosed condition.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'joint-comfort',
    heading: 'Joint comfort and everyday mobility',
    body: (
      <>
        <p>The joint-comfort space is where curcumin gets the most public attention in the UK. A series of published trials (including controlled trials on BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup>) have explored its role in everyday joint comfort over 8–12 week dosing periods.</p>
        <p>Under MHRA and ASA rules we can describe BCM-95® as a food supplement that may be taken as part of a routine to support everyday joint comfort and an active lifestyle. We <em>cannot</em> say it cures, treats, or "fixes" arthritis, osteoarthritis, or any other diagnosed condition. If you have a diagnosed joint condition, your GP is the right starting point.</p>
      </>
    ),
  },
  {
    id: 'mood-cognition',
    heading: 'Mood balance, focus and cognitive ageing — what has been studied?',
    body: (
      <>
        <p>Several published trials have explored curcumin's role in mood and cognitive function in adults. A widely-cited paper:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Lopresti AL, Maes M, Maker GL, Hood SD, Drummond PD. <em>Curcumin for the treatment of major depression: a randomised, double-blind, placebo-controlled study.</em> J Affect Disord. 2014 Oct;167:368–75. <a href="https://doi.org/10.1016/j.jad.2014.06.001" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">doi:10.1016/j.jad.2014.06.001</a></li>
        </ul>
        <p>Cochrane has an active interest in the area too — a Cochrane Central Register entry exists for a BCM-95® controlled trial.</p>
        <p>The honest takeaway: the research is interesting and ongoing, but a food supplement is not a treatment. People exploring mood-balance routines often combine consistent sleep, daily movement, sunlight exposure, omega-3 and adequate vitamin D — and may add a curcumin food supplement on top.</p>
      </>
    ),
  },
  {
    id: 'antioxidant',
    heading: 'Antioxidant and polyphenol context',
    body: (
      <>
        <p>Curcumin is a polyphenol, the broad class of plant compounds that includes resveratrol, quercetin, the catechins in green tea, the anthocyanins in berries, and the oleocanthal in extra-virgin olive oil. Polyphenol-rich diets correlate strongly with healthy ageing markers in epidemiological literature.</p>
        <p>You can think of a daily curcumin food supplement as one additional, concentrated polyphenol layer on a Mediterranean-style baseline — not a magic ingredient that works in isolation.</p>
      </>
    ),
  },
  {
    id: 'how-much',
    heading: 'How much curcumin per day, and for how long?',
    body: (
      <>
        <p>Most published curcumin trials use daily doses in the <strong>500–1500 mg curcuminoids</strong> range, taken consistently for <strong>8–12 weeks</strong>. The 2-capsule BCM-95® serving sits comfortably in this evidence-backed range, delivering 650 mg of standardised curcumin (within 1000 mg of Curcugreen<sup>&reg;</sup> extract).</p>
        <p>Take it with food — ideally a meal containing a little fat. Be consistent. Don't expect day-one results; this is a routine-builder, not a quick fix.</p>
      </>
    ),
  },
  {
    id: 'safety',
    heading: 'Safety, side-effects and who should be cautious',
    body: (
      <>
        <p>Curcumin food supplements are generally well tolerated in healthy adults. Reported low-frequency side-effects in trials are mild and gastrointestinal (looser stools, mild stomach upset), usually resolving by reducing the dose or taking it with food.</p>
        <p>Speak to your GP or pharmacist <em>before</em> starting a curcumin food supplement if you:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Take blood-thinning medication (warfarin, DOACs, antiplatelets)</li>
          <li>Have a gallbladder condition or gallstones</li>
          <li>Are pregnant or breastfeeding</li>
          <li>Are scheduled for surgery within 2 weeks</li>
          <li>Take prescription medication metabolised by CYP3A4 — although the BCM-95® / Curcugreen<sup>&reg;</sup> formulation does not contain piperine, individual interactions are always worth checking</li>
        </ul>
      </>
    ),
  },
  {
    id: 'wrap',
    heading: 'Bottom line for a UK buyer',
    body: (
      <>
        <p>Cooking with turmeric is delicious but won't move the curcumin-dose needle very far. A clinically-studied curcumin food supplement is the practical way to add a meaningful, daily, repeatable polyphenol layer to an active life.</p>
        <p>If you want one product that captures the most-studied curcumin formulation in clean-label, vegan capsules with all-inclusive UK pricing, BCM-95® by IIY Phytoventra is built for exactly that.</p>
      </>
    ),
  },
];

export default function TurmericCurcuminBenefitsUK() {
  return (
    <LandingArticle
      title="Turmeric & Curcumin Benefits — A UK Evidence Guide | IIY Phytoventra"
      description="Plain-English UK guide to turmeric and curcumin: active living, joint comfort, mood balance, polyphenol context. Doses, safety, who should be cautious. MHRA/ASA compliant."
      pathOverride="/turmeric-curcumin-benefits-uk"
      heading="Turmeric & Curcumin Benefits: A UK Evidence Guide"
      subheading="What does daily curcumin actually do for an active UK adult? An honest, MHRA-compliant walkthrough of the published evidence, dosing, safety, and how to make it a sustainable routine."
      tag="Evidence Guide"
      datePublished="2026-02-10"
      sections={sections}
    />
  );
}
